<template>
  <div v-if="tokenValid && !tokenExpired" class="text-center painel">
    <background-wrapper>
      <div>
        <h1 class="mb-4">Cadastrar senha</h1>
        <h2 class="brand-primary--text mb-2">{{ userName }}</h2>
      </div>
      <cs-message v-show="responseErrors.length > 0" :value="responseErrors" class="errorMessage" />
      <v-form class="resetForm">
        <cs-text-field
          v-model="password"
          placeholder="Senha"
          :error-messages="passwordErrors"
          @change="validateField"
          password
          required
          @input="matchRegex"
        />
        <cs-text-field
          id="password"
          @change="validadeConfirmPassword"
          v-model="confirmPassword"
          :error-messages="confirmPasswordErrors"
          placeholder="Repetir senha"
          required
          password
        />
        <PassWordCheck :password="password" :length="passwordLength" />
        <div id="termsOfService" class="bottomSpace">
          <terms-of-service
            @onAcceptTerms="handleAcceptTerms"
            @onDialogAccepted="handleDialogAcceptedTerms"
          ></terms-of-service>
        </div>
        <v-icon icon="$vuetify"></v-icon>
        <cs-recaptcha
          id="recaptcha"
          ref="vue_recaptcha"
          v-if="!captchaNotRequired"
          v-model="recaptcha"
          :sitekey="global.recaptchaKey"
        />
        <br />
        <request
          id="resetPassword_btn"
          method="post"
          class="w-100"
          :style="{ color: !formIsInvalid ? '#fff' : '#888888 !important' }"
          :headers="headers"
          :params="params"
          :url="global.resetPasswordUrl"
          :value="payload"
          :disabled="formIsInvalid"
          @resolved="resolved"
        >
          Criar conta
        </request>
      </v-form>
    </background-wrapper>
  </div>
  <div v-else>
    <resend-access
      :token="token"
      :url="global.sendEmailResetPasswordUrl"
      :expiredPassword="expiredPassword"
    ></resend-access>
  </div>
</template>

<script>
import BackgroundWrapper from '@/components/background/BackgroundWrapper.vue'
import loginService from '@/services/loginService'
import Util from '@/components/Util.js'
import csMessage from '@/components/campos/csMessage.vue'
import csRecaptcha from '@/components/campos/csRecaptcha.vue'
import csTextField from '@/components/campos/csTextField.vue'
import Request from '@/components/Request.vue'
import resendAccess from '@/components/firstAccess/resendAccess.vue'
import termsOfService from '@/components/TermsOfService/termsOfService.vue'
import { recaptchaDisabledUrls } from '@/constants'

import { mapState } from 'vuex'
import { required, maxLength, minLength } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'

const strongPassword = (password) => Util.validateStrongPassword(password)
const consecutiveCharactersPassword = (password) => Util.validateConsecutiveCharacters(password)
const containsReservedWords = (password) => Util.validatePasswordContainsReservedWords(password)

export default {
  mixins: [loginService, validationMixin],
  validations() {
    return {
      password: {
        required,
        strongPassword,
        minLength: minLength(8),
        maxLength: maxLength(12),
        consecutiveCharactersPassword,
        containsReservedWords
      },
      confirmPassword: { required }
    }
  },
  components: {
    BackgroundWrapper,
    csRecaptcha,
    csMessage,
    csTextField,
    Request,
    resendAccess,
    termsOfService
  },
  props: {
    token: {
      type: String,
      required: true
    },
    fullName: {
      type: String,
      required: true,
      default: ''
    },
    tokenValid: {
      type: Boolean,
      required: true
    },
    expiredPassword: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      recaptcha: '',
      captchaRequired: true,
      loading: false,
      tokenExpired: false,
      responseErrors: [],
      message: '',
      requiredLength: false
    }
  },
  mounted() {
    this.checkCaptchaRequirement()
  },
  computed: {
    ...mapState(['global']),
    captchaNotRequired() {
      var isDisabled = recaptchaDisabledUrls.some((u) => window.location.hostname === u)
      return isDisabled || !this.captchaRequired
    },
    passwordLength() {
      if (this.password.length > 7 && this.password.length <= 12) return !this.requiredLength
    },
    passwordErrors() {
      const errors = []
      if (!this.$v.password.$dirty) return errors
      !this.$v.password.required && errors.push('Esse campo é obrigatório.')
      if (errors.length > 0) return errors
      !this.$v.password.strongPassword &&
        errors.push(
          'A senha deve ter letras, números e ao menos um caracter especial (ex: @, #, $)'
        )
      !this.$v.password.consecutiveCharactersPassword &&
        errors.push('A senha não deve possuir 3 caracteres sequenciais consecutivos iguais')
      !this.$v.password.containsReservedWords &&
        errors.push('A senha não deve possuir as palavras senha, mudar ou password')
      !this.$v.password.minLength && errors.push('A senha deve ter entre 8 e 12 caracteres')
      !this.$v.password.maxLength && errors.push('A senha deve ter entre 8 e 12 caracteres')

      return errors
    },
    confirmPasswordErrors() {
      const errors = []
      if (!this.$v.confirmPassword.$dirty) return errors
      !this.$v.confirmPassword.required && errors.push('Esse campo é obrigatório.')
      if (this.password !== this.confirmPassword) {
        errors.push('As senhas não correspondem. Tente novamente.')
        return errors
      }
      return errors
    },
    formIsInvalid() {
      const fakeKeyRepactcha = 'f9c7af7ebcbf098b9f5f37361d1b168bb2e5b98d930ceef0f055377a8c94db6'
      if (this.captchaNotRequired) this.recaptcha = fakeKeyRepactcha
      return !(
        this.password &&
        this.confirmPassword &&
        this.recaptcha !== '' &&
        this.passwordErrors.length === 0
      )
    },
    userName() {
      const name = this.fullName.trim().split(' ')
      return name.length === 1 ? name[0] : name[0] + ' ' + name[name.length - 1]
    },
    util() {
      return Util
    },
    headers() {
      const headerContent = { 'Content-Type': 'application/json' }
      if (this.requestToken) {
        headerContent.RequestVerificationToken = this.requestToken
      }
      return headerContent
    },
    params() {
      return this.$route.query
    },
    payload() {
      return {
        password: this.password,
        confirmPassword: this.confirmPassword,
        recaptcha: this.recaptcha,
        passwordToken: this.token
      }
    }
  },
  methods: {
    handleAcceptTerms(accept) {
      if (accept) {
        this.termsAccepted = true
      } else {
        this.termsAccepted = false
      }
    },
    handleDialogAcceptedTerms() {
      this.handleAcceptTerms(true)
      if (!this.formIsInvalid) {
        this.$nextTick(() => {
          this.sendRequest()
        })
      }
    },
    resolved(response) {
      this.responseErrors = []
      if (!response.data?.errors) {
        this.$router.push({ name: 'home', params: { changedPass: true } })
        return
      }
      if (response.data?.errors?.InvalidToken) {
        this.tokenExpired = true
      } else if (response.data?.errors?.ConfirmPassword) {
        this.responseErrors.push(response.data.errors.ConfirmPassword[0])
      } else if (response.data?.errors?.Password) {
        this.responseErrors.push(response.data.errors.Password[0])
      } else {
        this.responseErrors.push('Ocorreu um erro. Tente novamente')
      }
      this.$refs.vue_recaptcha.reset()
      this.recaptcha = ''
    },
    validateField() {
      this.responseErrors = []
      this.$v.password.$touch()
    },
    validadeConfirmPassword() {
      this.responseErrors = []
      this.$v.confirmPassword.$touch()
    },
    checkCaptchaRequirement() {
      this.$http.get('/api/v3/account/captcha-required').then((response) => {
        this.captchaRequired = response.data.isRequired
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.resetForm,
.errorMessage {
  width: 100%;
  max-width: 304px;
}

#recaptcha {
  margin: 8px !important;
}

#password {
  ::v-deep .v-input {
    margin-bottom: 1rem !important;
  }
}

h1 {
  font-size: 24px;
  text-align: center;
  text-transform: uppercase;
}
</style>
